import React, { useState, useEffect, useRef } from 'react';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  Typography,
  Box,
  Container,
  Tabs,
  Tab,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  Snackbar,
  Alert,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Print as PrintIcon, Save as SaveIcon, Description as DescriptionIcon } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ViewListIcon from '@mui/icons-material/ViewList';
import ArticleIcon from '@mui/icons-material/Article';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import withDarkModeCallsheetStyles from '../../styles/withDarkModeCallsheetStyles';
import TabPanel from '../common/TabPanel';
import CallsheetHeader from './CallsheetHeader';
import CrewCalls from './CrewCalls';
import LocationInfo from './LocationInfo';
import Schedule from './Schedule';
import TalentCalls from './TalentCalls';
import Notes from './Notes';
import EmergencyContacts from './EmergencyContacts';
import CallsheetHistory from './CallsheetHistory';
import CallsheetComposeView from './CallsheetComposeView';
import useCallsheetData from './hooks/useCallsheetData';
import { printCallsheet, getCallsheetByDay } from './utils/callsheetHelpers';

function a11yProps(index) {
  return {
    id: `callsheet-tab-${index}`,
    'aria-controls': `callsheet-tabpanel-${index}`,
  };
}

// Main Callsheet component that composes all the sub-components
const CallsheetMain = ({ projectId, projectName = '', styles }) => {
  const [value, setValue] = useState(0);
  const [locationsVersion, setLocationsVersion] = useState(0);
  const [viewMode, setViewMode] = useState('form'); // 'form' or 'compose'
  const theme = useTheme();
  const { can } = usePermissions();
  const canView = can('callsheets.view');
  const canManage = can('callsheets.manage');
  const canWrite = canManage; // Use permission instead of state
  
  const {
    callsheet,
    callsheetRef,
    loading,
    error,
    saveStatus,
    lastAutoSave,
    updateCallsheet,
    updateSchedule,
    updateTalentCalls,
    updateCrewCalls,
    updateEmergencyContacts,
    updateAdditionalLocations,
    handleSaveCallsheet,
    loadCallsheet,
    resetCallsheet
  } = useCallsheetData(projectId, projectName);

  // Debounced autosave for background/stand-ins
  const bgSaveTimer = useRef(null);
  const handleBackgroundUpdate = (bg) => {
    updateCallsheet('background', bg);
    if (bgSaveTimer.current) {
      clearTimeout(bgSaveTimer.current);
    }
    bgSaveTimer.current = setTimeout(() => {
      handleSaveCallsheet();
    }, 800);
  };

  // Debounced autosave for parkingLocations
  const parkingSaveTimer = useRef(null);
  const parkingFirstRun = useRef(true);
  useEffect(() => {
    // Skip autosave on the very first run to avoid saving immediately after initial load
    if (parkingFirstRun.current) {
      parkingFirstRun.current = false;
      return;
    }
    if (!Array.isArray(callsheet?.parkingLocations)) return;
    if (parkingSaveTimer.current) clearTimeout(parkingSaveTimer.current);
    parkingSaveTimer.current = setTimeout(() => {
      handleSaveCallsheet();
    }, 1000);
    return () => {
      if (parkingSaveTimer.current) clearTimeout(parkingSaveTimer.current);
    };
  }, [callsheet?.parkingLocations]);

  // Debounced autosave for scheduleInfo times (shooting call, lunch, etc.)
  const schedInfoSaveTimer = useRef(null);
  const schedInfoFirstRun = useRef(true);
  useEffect(() => {
    // Skip autosave on initial mount
    if (schedInfoFirstRun.current) {
      schedInfoFirstRun.current = false;
      return;
    }
    if (!callsheet?.scheduleInfo) return;
    if (schedInfoSaveTimer.current) clearTimeout(schedInfoSaveTimer.current);
    schedInfoSaveTimer.current = setTimeout(() => {
      handleSaveCallsheet();
    }, 800);
    return () => {
      if (schedInfoSaveTimer.current) clearTimeout(schedInfoSaveTimer.current);
    };
  }, [callsheet?.scheduleInfo]);

  // Debounced autosave for producers list
  const producersSaveTimer = useRef(null);
  const producersFirstRun = useRef(true);
  useEffect(() => {
    if (producersFirstRun.current) {
      producersFirstRun.current = false;
      return;
    }
    if (!Array.isArray(callsheet?.producers)) return;
    if (producersSaveTimer.current) clearTimeout(producersSaveTimer.current);
    producersSaveTimer.current = setTimeout(() => {
      handleSaveCallsheet();
    }, 800);
    return () => {
      if (producersSaveTimer.current) clearTimeout(producersSaveTimer.current);
    };
  }, [callsheet?.producers]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleLocationsChanged = () => {
    setLocationsVersion((prev) => prev + 1);
  };

  // We'll use a ref to track if we've already loaded schedule data
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const srOnly = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  };
  
  // Load schedule data only once when component mounts
  useEffect(() => {
    if (!scheduleLoaded && projectId && callsheet) {
      console.log('Pre-loading schedule data for printing (one-time load)');
      // We'll set a flag to prevent multiple loads
      setScheduleLoaded(true);
    }
  }, [projectId, callsheet, scheduleLoaded]);

  useEffect(() => {
    if (saveStatus.success) {
      setShowSaveSuccess(true);
    } else if (!saveStatus.loading) {
      setShowSaveSuccess(false);
    }
  }, [saveStatus.success, saveStatus.loading]);

  const handleCloseSaveSuccess = (_, reason) => {
    if (reason === 'clickaway') return;
    setShowSaveSuccess(false);
  };

  const handlePrint = async () => {
    // Debug the callsheet data, specifically checking for nextDaySchedule
    console.log('Printing callsheet with data (pre-wait):', callsheet);
    console.log('Main schedule data (pre-wait):', callsheet.schedule);
    console.log('Next day schedule data (pre-wait):', callsheet.nextDaySchedule);

    // Give React a brief moment to commit any in-flight state updates (e.g., freshly typed Weather Notes)
    await new Promise(resolve => setTimeout(resolve, 75));

    // Re-read from state after the tick
    let cs = callsheetRef.current || callsheet;

    // Ensure parkingLocations are present by fetching from server if missing
    try {
      const dayNum = parseInt(cs?.shootingDay) || 1;
      if ((!Array.isArray(cs?.parkingLocations) || cs.parkingLocations.length === 0) && projectId) {
        const dayData = await getCallsheetByDay(projectId, dayNum);
        if (dayData && Array.isArray(dayData.parkingLocations) && dayData.parkingLocations.length > 0) {
          updateCallsheet({ parkingLocations: dayData.parkingLocations });
          // wait a tick and refresh cs
          await new Promise(r => setTimeout(r, 30));
          cs = callsheetRef.current || { ...cs, parkingLocations: dayData.parkingLocations };
        }
        // If still empty, try localStorage fallbacks here as well
        if (!Array.isArray(cs?.parkingLocations) || cs.parkingLocations.length === 0) {
          const tryLoadLocalParking = () => {
            let parking = [];
            try {
              const key = `parkingLocations_${projectId}_${dayNum}`;
              const raw = localStorage.getItem(key);
              const parsed = raw ? JSON.parse(raw) : [];
              if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch {}
            try {
              const suffix = `_${dayNum}`;
              for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (!k) continue;
                if (k.startsWith('parkingLocations_') && k.endsWith(suffix)) {
                  const raw = localStorage.getItem(k);
                  const parsed = raw ? JSON.parse(raw) : [];
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed;
                  }
                }
              }
            } catch {}
            return parking;
          };
          const localParking = tryLoadLocalParking();
          if (Array.isArray(localParking) && localParking.length > 0) {
            updateCallsheet({ parkingLocations: localParking });
            await new Promise(r => setTimeout(r, 30));
            cs = callsheetRef.current || { ...cs, parkingLocations: localParking };
          }
        }
      }
    } catch (e) {
      // non-blocking if fetch fails
    }
    console.log('Printing callsheet with data (post-wait):', cs);
    console.log('Main schedule data (post-wait):', cs.schedule);
    console.log('Next day schedule data (post-wait):', cs.nextDaySchedule);
    
    // Check if we need to load schedule data
    const hasMainSchedule = cs.schedule && cs.schedule.length > 0;
    const hasNextDaySchedule = cs.nextDaySchedule && cs.nextDaySchedule.length > 0;
    
    if (!hasMainSchedule || !hasNextDaySchedule) {
      console.log(`Missing schedule data - Main: ${hasMainSchedule ? 'Loaded' : 'Not loaded'}, Next day: ${hasNextDaySchedule ? 'Loaded' : 'Not loaded'}`);
      // Print anyway - we'll add a note in the printCallsheet function
      // to inform the user that they need to visit the Schedule tab first
      printCallsheet(cs, 'legal', '0.2cm', true, true);
    } else {
      // Both schedules are loaded, print immediately
      console.log('Both schedules loaded, printing complete callsheet');
      printCallsheet(cs, 'legal', '0.2cm');
    }
  };

  const handleSave = async () => {
    try {
      const result = await handleSaveCallsheet();
      if (!result) return;
    } catch (err) {
      console.error('Error saving callsheet:', err);
      const status = err?.response?.status || err?.status;
      if (status === 403) {
        // Permission denied - user doesn't have callsheets.manage
        console.warn('Permission denied: User does not have callsheets.manage permission');
      }
    }
  };

  // Advance Day UI removed per request; server endpoint remains available for future use

  if (loading && !callsheet) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: theme.palette.background.default
      }}>
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  if (error && !callsheet) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        <Typography variant="h6">Error</Typography>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  // Permission check - show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          You don't have permission to view call sheets. Contact your project administrator for access.
        </Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} disableGutters sx={{ ...(styles?.container || {}), px: 0 }}>
      {/* Error and success messages */}
      {error && (
        <Box sx={{ p: 2, mb: 2, backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.dark, 0.2) : alpha(theme.palette.error.light, 0.2), borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSaveSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSaveSuccess} severity="success" sx={{ width: '100%' }}>
          Callsheet saved successfully!
        </Alert>
      </Snackbar>

      {/* Main content container */}
      <Box sx={{
        ...styles.paper,
        width: '100%',
        maxWidth: 'none',
        typography: 'body1',
        padding: 4,
        marginTop: 2,
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'visible',
        position: 'relative'
      }}>
        {/* Header section with title and action buttons */}
        <Paper 
          elevation={0} 
          sx={{
            mb: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            backgroundColor: (theme) => theme.palette.background.default,
            color: (theme) => theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary,
            p: { xs: 2, md: 2 },
            borderRadius: 0,
            position: 'relative',
            overflow: 'hidden',
            borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                background: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DescriptionIcon sx={{ color: 'primary.main', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Call Sheet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {`Day ${callsheet?.shootingDay || 1}`}
                {callsheet?.date ? ` • ${new Date(callsheet.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              flexShrink: 0,
              alignItems: 'flex-end',
              alignSelf: 'flex-end',
              justifyContent: 'flex-end'
            }}
          >
              <Tooltip title="Save changes" arrow>
                <span>
                  <IconButton
                    onClick={handleSave}
                    disabled={!canWrite}
                    aria-label="Save changes"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      border: `1px solid ${alpha('#fff', 0.2)}`,
                      color: alpha('#fff', 0.9),
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: alpha('#fff', 0.12)
                      }
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Print call sheet" arrow>
                <span>
                  <IconButton
                    onClick={handlePrint}
                    aria-label="Print call sheet"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      border: `1px solid ${alpha('#fff', 0.2)}`,
                      color: alpha('#fff', 0.9),
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: alpha('#fff', 0.12)
                      }
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {/* View Mode Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
                sx={{
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.25)
                    : alpha('#fff', 0.5),
                  overflow: 'hidden',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.35)
                    : alpha('#fff', 0.15),
                  '& .MuiToggleButton-root': {
                    color: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.75)
                      : alpha('#fff', 0.85),
                    border: 'none',
                    px: 2.5,
                    py: 0.75,
                    textTransform: 'none',
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.45)
                        : alpha('#fff', 0.35),
                      color: '#fff',
                      boxShadow: theme.palette.mode === 'dark'
                        ? 'inset 0 0 12px rgba(0,0,0,0.35)'
                        : 'inset 0 0 12px rgba(255,255,255,0.25)',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.primary.main, 0.55)
                          : alpha('#fff', 0.45)
                      }
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.25)
                        : alpha('#fff', 0.25)
                    }
                  }
                }}
              >
                <ToggleButton value="form">
                  <ViewListIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Form
                </ToggleButton>
                <ToggleButton value="compose">
                  <ArticleIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Compose
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
        </Paper>
        
        {/* Compose View */}
        {viewMode === 'compose' && (
          <CallsheetComposeView
            data={callsheet}
            onUpdate={updateCallsheet}
            onPrint={handlePrint}
            projectId={projectId}
            locationsVersion={locationsVersion}
          />
        )}

        {/* Form View (Tabs) */}
        {viewMode === 'form' && (
        <>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.divider, 0.2)
            : 'divider',
          mt: 2
        }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="callsheet tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              ...styles.tabs,
              '& .MuiTab-root': {
                ...styles.tab,
                transition: 'all 0.2s ease',
                fontWeight: 400,
                fontSize: '0.8rem',
                minHeight: 40,
                padding: '6px 12px',
                textTransform: 'none',
                color: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.6)
                  : theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
                  color: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.9)
                    : theme.palette.text.primary
                },
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 500
                }
              },
              '& .MuiTabs-indicator': {
                height: 2,
                backgroundColor: theme.palette.primary.main
              }
            }}
          >
            <Tab label="Header" {...a11yProps(0)} sx={styles.tab} />
            <Tab label="Location" {...a11yProps(1)} sx={styles.tab} />
            <Tab label="Schedule" {...a11yProps(2)} sx={styles.tab} />
            <Tab label="Talent Calls" {...a11yProps(3)} sx={styles.tab} />
            <Tab label="Crew Calls" {...a11yProps(4)} sx={styles.tab} />
            <Tab label="Notes" {...a11yProps(5)} sx={styles.tab} />
            <Tab label="Emergency" {...a11yProps(6)} sx={styles.tab} />
            <Tab label="History" {...a11yProps(7)} sx={styles.tab} />
          </Tabs>
        </Box>
        {/* Interaction blocker when read-only: blocks edits in tab content while allowing header buttons above */}
        {!canWrite && (
          <Box
            sx={{
              position: 'absolute',
              top: 160, // below header/buttons area
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              background: 'transparent'
            }}
            onClick={(e) => { e.stopPropagation(); }}
          />
        )}
        <TabPanel value={value} index={0}>
          <CallsheetHeader 
            data={callsheet}
            onUpdate={updateCallsheet} 
            projectId={projectId}
            styles={styles}
          />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <LocationInfo 
            projectId={projectId}
            onLocationsChanged={handleLocationsChanged}
          />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <Schedule 
            data={callsheet} 
            onUpdate={updateSchedule} 
            projectId={projectId}
            locationsVersion={locationsVersion}
            styles={styles}
          />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <TalentCalls 
            data={callsheet} 
            onUpdate={updateTalentCalls} 
            projectId={projectId}
            styles={styles}
            onUpdateBackground={handleBackgroundUpdate}
          />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <CrewCalls 
            data={callsheet} 
            onUpdate={updateCrewCalls}
            projectId={projectId}
            styles={styles}
          />
        </TabPanel>
        <TabPanel value={value} index={5}>
          <Notes 
            data={callsheet} 
            onUpdate={updateCallsheet}
            projectId={projectId}
            styles={styles}
          />
        </TabPanel>
        <TabPanel value={value} index={6}>
          <EmergencyContacts 
            data={callsheet.emergencyContacts || {}} 
            onUpdate={updateEmergencyContacts}
            initialLocation={callsheet.mainLocationAddress || ''}
            styles={styles}
            projectId={projectId}
          />
        </TabPanel>
        <TabPanel value={value} index={7}>
          <CallsheetHistory 
            projectId={projectId}
            loadCallsheet={loadCallsheet}
            resetCallsheet={resetCallsheet}
            styles={styles}
          />
        </TabPanel>
        </>
        )}
      </Box>
      </Container>
    </LocalizationProvider>
  );
};

// Wrap the Callsheet component with the dark mode styles
export default withDarkModeCallsheetStyles(CallsheetMain);
